import { IAgencyCodeRow, IAgencyDetailsResponse, mapAgencyStatus } from './AgencyCodesModels';
import { GetStateName } from 'Foundation/Common/StateHelper';

const AGENCY_DETAILS_URL = '/api/sitecore/KyberAgencyDetails/GetAgencyDetails';
// Bounded below the page's own USER_DATA_TIMEOUT_MS watchdog (10s) so a stalled Kyber call
// always resolves to the existing "unavailable" state instead of spinning indefinitely.
const AGENCY_DETAILS_TIMEOUT_MS = 8000;

/** Agency codes are compared without formatting so "12-34-5678" matches "12345678". */
function normalizeCode(code: string | null | undefined): string {
  return code?.replace(/-/g, '').trim() ?? '';
}

function joinAddressLines(line1: string | null, line2: string | null): string {
  return [line1?.trim(), line2?.trim()].filter((line) => !!line).join(', ');
}

function getDisplayState(stateAbbreviation: string | null | undefined): string {
  const state = stateAbbreviation?.trim() ?? '';
  return GetStateName(state) || state;
}

/** Returns null when the call fails so the UI can distinguish outage from empty data. */
export async function getAgencyDetails(): Promise<IAgencyDetailsResponse[] | null> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), AGENCY_DETAILS_TIMEOUT_MS);

  try {
    const res = await fetch(AGENCY_DETAILS_URL, { method: 'GET', signal: controller.signal });

    if (!res?.ok) {
      return null;
    }

    const contentType = res.headers?.get('content-type');
    if (!contentType || contentType.indexOf('application/json') === -1) {
      return null;
    }

    const data = await res.json();
    return Array.isArray(data) ? data : null;
  } catch (error) {
    console.error('Error retrieving agency details', error);
    return null;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

//Combines the agency/eCLIQ codes already on the user session with the Kyber
//agency details. Codes are paired by index.

export function buildAgencyCodeRows(
  agencyCodes: string[] | null | undefined,
  eCliqCodes: string[] | null | undefined,
  details: IAgencyDetailsResponse[] | null | undefined,
): IAgencyCodeRow[] {
  if (!agencyCodes?.length) return [];

  const detailsByCode = new Map<string, IAgencyDetailsResponse>();
  details?.forEach((detail) => {
    const key = normalizeCode(detail?.AgencyCode);
    if (key) detailsByCode.set(key, detail);
  });

  return agencyCodes
    // Pair eCLIQ by original index BEFORE filtering so blanks can't shift the pairing.
    .map((code, index) => ({ code, eCliqCode: eCliqCodes?.[index]?.trim() ?? '' }))
    .filter(({ code }) => !!code?.trim())
    .map(({ code, eCliqCode }) => {
      const normalizedCode = normalizeCode(code);
      const detail = detailsByCode.get(normalizedCode);

      return {
        agencyName: detail?.AgencyName?.trim() ?? '',
        agencyCode: code.trim(),
        eCliqCode,
        status: mapAgencyStatus(detail?.LineOfBusinessStatus),
        address: joinAddressLines(detail?.StreetAddressLine1 ?? null, detail?.StreetAddressLine2 ?? null),
        city: detail?.City?.trim() ?? '',
        state: getDisplayState(detail?.State),
        zipCode: detail?.Zip?.trim() ?? '',
      };
    });
}
